import React, { useCallback, useState } from "react"
import { Box, Button, Flex, Text, AspectRatio, Image } from "@chakra-ui/react"
import { useCart } from "@app/hooks/useCart"
import { useConfigContext } from "@app/providers/config"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useShopifyImage } from "@app/hooks/useShopifyImage"
import { CartUpsellProduct } from "@app/hooks/useCartUpsell"

export const CartUpsellProductCard: React.FC<{ product: CartUpsellProduct }> = React.memo(({ product }) => {
  const { addToCart, loading } = useCart()
  const { formattedPrice, formattedCompareAtPrice, onSale } = useShopifyPrice(product?.variants?.[0])
  const { scaledUrl: variantImage } = useShopifyImage(product.variants?.[0]?.image?.src, "112x142")
  const [btnDisabled, setBtnDisabled] = useState(false)
  const {
    settings: { cart },
  } = useConfigContext()

  const handleAddToCart = useCallback(async () => {
    if (!loading && !btnDisabled) {
      setBtnDisabled(true)
      await addToCart(product, product?.variants?.[0], 1, [
        { key: cart.giftUpsellText, value: product?.additionalText },
        { key: cart.giftUpsellTextColor, value: product?.additionalTextColor },
      ])
      setBtnDisabled(false)
    }
  }, [loading, btnDisabled, addToCart, product, cart.giftUpsellText, cart.giftUpsellTextColor])

  return product ? (
    <Flex
      pos="relative"
      alignItems="center"
      justifyContent="space-between"
      bg="grey.lightest"
      border="1px solid"
      borderColor="primary.softGrey"
      borderRadius="12px"
      padding="8px 12px"
    >
      <Flex flex="1" minWidth="0" gap={3} alignItems="flex-start" pr="75px">
        <Box width="56px" flexShrink={0} borderRadius="4px" bg="primary.white">
          <AspectRatio ratio={56 / 71}>
            {variantImage ? <Image src={variantImage} w="100%" h="100%" style={{ objectFit: "unset" }} borderRadius="4px" /> : <Box />}
          </AspectRatio>
        </Box>
        <Flex flexDirection="column" gap={1} alignItems="flex-start">
          <Flex flexDirection="column" gap="0" alignItems="flex-start">
            {product?.additionalText && (
              <Text
                fontSize="0.75rem"
                lineHeight={1.3333}
                fontWeight="medium"
                textTransform="uppercase"
                color={product?.additionalTextColor}
              >
                {product.additionalText}
              </Text>
            )}
            {product?.title && <Text size="md">{product.title}</Text>}
          </Flex>
          <Flex direction="row">
            {onSale && (
              <Text size="lg" textDecoration="line-through" color="secondary.grey" mr={1.5}>
                {formattedCompareAtPrice}
              </Text>
            )}
            <Text size="lg" color={onSale ? "text.error" : "text.primary"}>
              {formattedPrice}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Button
        pos="absolute"
        bottom="11px"
        right="11px"
        height="35px"
        width="64px"
        minWidth="64px"
        padding="0 24px"
        borderRadius="40px"
        variant="secondary"
        size="small"
        onClick={() => handleAddToCart()}
        isDisabled={btnDisabled}
        data-loading={btnDisabled || undefined}
      >
        {btnDisabled ? "Adding" : "Add"}
      </Button>
    </Flex>
  ) : null
})
